import { Component, ChangeDetectionStrategy, computed, inject, signal, OnInit, OnDestroy, DOCUMENT } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { TagModule } from 'primeng/tag';
import { InputMaskModule } from 'primeng/inputmask';
import { FloatLabelModule } from 'primeng/floatlabel';
import { SelectModule } from 'primeng/select';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { environment } from '../../../../environments/environment';
import { SubscriptionService } from '../../../services/subscription.service';
import type {
  NetPay3dsSdk,
  NetPaySdk,
  NetPayToken,
  NetPayTokenResponse,
  NetPayTokenResponseData
} from '../../../utils/netpay-token';
import {
  type CustomerCardDTO,
  type PlanItemDTO,
  type PublicSubscriptionCheckoutRequest,
  type PublicSubscriptionDetailDTO,
  type SATCFDIUseDTO,
  type SubscriptionDTO,
  type ThreeDSChallengeDTO
} from '../../../core/models';

type CardPaymentMode = 'existing' | 'new';

@Component({
  selector: 'app-public-netpay-subscription-checkout',
  templateUrl: './public-netpay-subscription-checkout.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TagModule,
    InputMaskModule,
    FloatLabelModule,
    SelectModule,
    ConfirmDialogModule
  ],
  providers: [ConfirmationService]
})
export class PublicNetpaySubscriptionCheckoutComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private document = inject(DOCUMENT);
  private subscriptionService = inject(SubscriptionService);
  private fb = inject(FormBuilder);
  private confirmationService = inject(ConfirmationService);
  private NetPayElement: HTMLScriptElement | null = null;
  private netpay3dsElement: HTMLScriptElement | null = null;
  private netpayReadyWait: Promise<void> | null = null;

  /**
   * Contexto interno (`_this`) que entrega la SDK {@code netpay3ds} como primer
   * argumento del callback de {@code config()} (es decir, dentro de nuestro
   * {@code callback3ds}). Este es el contexto que {@code proceed()} necesita para
   * abrir el iframe del ACS; el {@code _this} del callback de {@code init()} llega
   * {@code undefined} y no sirve para {@code proceed}.
   */
  private netpay3dsContext: unknown = null;

  /**
   * Máximo de tarjetas guardadas que un cliente puede tener para este flujo. Debe ir
   * alineado con la constante {@code MAX_CUSTOMER_CARDS_PER_PROVIDER} del backend.
   */
  readonly maxCustomerCards = 3;

  detail = signal<PublicSubscriptionDetailDTO | null>(null);
  subscription = signal<SubscriptionDTO | null>(null);
  customerCards = signal<CustomerCardDTO[]>([]);
  cardPaymentMode = signal<CardPaymentMode>('new');
  selectedCardId = signal<number | null>(null);
  loading = signal(true);
  error = signal(false);
  errorMessage = signal('');
  checkoutLoading = signal(false);
  checkoutSuccess = signal(false);
  netpay3dsReferenceId = signal<string | null>(null);
  userAgent = '';
  netpayDeviceFingerPrint = signal<string | null>(null);
  deletingCardId = signal<number | null>(null);
  satCfdiUseOptions = signal<SATCFDIUseDTO[]>([]);
  catalogsLoading = signal(false);

  satCfdiUseSelectOptions = computed(() =>
    this.satCfdiUseOptions()
      .filter((use) => use.id != null)
      .map((use) => ({
        id: use.id!,
        label: this.formatCfdiUseLabel(use)
      }))
  );

  checkoutForm: FormGroup;

  constructor() {
    this.checkoutForm = this.fb.group({
      requiresInvoice: [false],
      cfdiUseId: [null as string | null],
      cardholderName: ['', [Validators.required, Validators.maxLength(200)]],
      cardNumber: ['', [Validators.required, Validators.minLength(13), Validators.maxLength(19)]],
      cvv: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(4)]],
      expirationDate: ['', [Validators.required, Validators.pattern(/^(0[1-9]|1[0-2])\/\d{2}$/)]]
    });

    this.checkoutForm.get('requiresInvoice')?.valueChanges.subscribe((requires: boolean) => {
      this.applyInvoiceValidators(requires);
    });
  }

  async ngOnInit(): Promise<void> {
    await this.initializeNetpaySdk();

    if (this.document.defaultView) {
      this.userAgent = navigator.userAgent;
    }

    const token = this.route.snapshot.paramMap.get('token');

    if (!token) {
      this.loading.set(false);
      this.error.set(true);
      return;
    }

    this.subscriptionService.getPublicSubscription(token).subscribe({
      next: (res) => {
        this.loading.set(false);
        if (!res?.body) {
          this.error.set(true);
          return;
        }
        this.detail.set(res.body);
        this.subscription.set(res.body.subscription ?? null);

        const cards = res.body.customerCards ?? [];
        this.customerCards.set(cards);
        if (cards.length > 0) {
          this.cardPaymentMode.set('existing');
          this.selectedCardId.set(cards[0].id ?? null);
          this.applyPaymentModeValidators('existing');
        } else {
          this.cardPaymentMode.set('new');
          this.applyPaymentModeValidators('new');
        }

        this.initInvoiceFormFromSubscription(res.body.subscription ?? null);
        this.loadPublicBillingCatalogs(token);
      },
      error: () => {
        this.loading.set(false);
        this.error.set(true);
      }
    });
  }

  private initInvoiceFormFromSubscription(sub: SubscriptionDTO | null): void {
    const bt = sub?.billingTerms;
    if (!bt?.cfdiUse?.id) {
      return;
    }
    this.checkoutForm.patchValue({
      requiresInvoice: true,
      cfdiUseId: bt.cfdiUse.id
    }, { emitEvent: true });
  }

  private loadPublicBillingCatalogs(token: string): void {
    this.catalogsLoading.set(true);
    this.subscriptionService.listPublicSatCfdiUses(token).subscribe({
      next: (r) => {
        this.satCfdiUseOptions.set(r.ok && r.body ? r.body : []);
        this.catalogsLoading.set(false);
      },
      error: () => {
        this.satCfdiUseOptions.set([]);
        this.catalogsLoading.set(false);
      }
    });
  }

  private applyInvoiceValidators(requiresInvoice: boolean): void {
    const cfdiUseId = this.checkoutForm.get('cfdiUseId');

    if (requiresInvoice) {
      cfdiUseId?.setValidators([Validators.required]);
    } else {
      cfdiUseId?.clearValidators();
      cfdiUseId?.setValue(null, { emitEvent: false });
    }

    cfdiUseId?.updateValueAndValidity({ emitEvent: false });
  }

  get requiresInvoice(): boolean {
    return !!this.checkoutForm.get('requiresInvoice')?.value;
  }

  formatCfdiUseLabel(use: SATCFDIUseDTO): string {
    const id = use.id?.trim();
    const desc = use.description?.trim() || use.name?.trim() || '—';
    //return id ? `${id} — ${desc}` : desc;
    return desc;
  }

  /**
   * Vuelve a consultar el detalle público de la suscripción para refrescar la
   * lista de tarjetas guardadas tras un intento de pago fallido.
   * <p>Si el backend ya persistió la tarjeta recién tokenizada (caso típico de
   * error 3DS o duplicado), evitamos que el usuario la registre dos veces:
   * cambiamos a modo "tarjeta guardada" y la seleccionamos por defecto.
   * Mantenemos {@code errorMessage} para que el usuario vea por qué falló.</p>
   */
  private refreshCustomerCardsAfterError(subscriptionToken: string): void {
    this.subscriptionService.getPublicSubscription(subscriptionToken).subscribe({
      next: (res) => {
        const body = res?.body;
        if (!body) return;

        this.detail.set(body);
        if (body.subscription) {
          this.subscription.set(body.subscription);
        }

        const cards = body.customerCards ?? [];
        this.customerCards.set(cards);

        if (cards.length === 0) return;

        const currentId = this.selectedCardId();
        const stillExists = currentId != null && cards.some(c => c.id === currentId);
        if (!stillExists) {
          this.selectedCardId.set(cards[0].id ?? null);
        }
        this.cardPaymentMode.set('existing');
        this.applyPaymentModeValidators('existing');
        this.checkoutForm.patchValue({
          cardholderName: '',
          cardNumber: '',
          expirationDate: ''
        }, { emitEvent: false });
      },
      error: () => { }
    });
  }

  ngOnDestroy(): void { }

  setCardPaymentMode(mode: CardPaymentMode): void {
    if (mode === 'new' && this.hasReachedCardLimit) {
      this.errorMessage.set(
        `Alcanzaste el máximo de ${this.maxCustomerCards} tarjetas guardadas. Elimina una para registrar otra.`);
      return;
    }
    this.cardPaymentMode.set(mode);
    this.applyPaymentModeValidators(mode);
    if (mode === 'existing' && this.selectedCardId() == null) {
      const first = this.customerCards()[0];
      if (first?.id != null) {
        this.selectedCardId.set(first.id);
      }
    }
  }

  selectCard(cardId: number | undefined): void {
    if (cardId == null) return;
    this.selectedCardId.set(cardId);
    this.cardPaymentMode.set('existing');
    this.applyPaymentModeValidators('existing');
  }

  /**
   * Abre un diálogo elegante de PrimeNG para confirmar la eliminación. El borrado real
   * se delega a {@link #executeDeleteCard(number)}; mantenemos esta función separada
   * para que el botón solo dispare la confirmación y el flujo asíncrono quede aislado.
   */
  deleteCard(cardId: number | undefined): void {
    if (cardId == null) return;
    if (this.deletingCardId() != null || this.checkoutLoading()) return;

    const card = this.customerCards().find(c => c.id === cardId);
    const label = card ? this.formatCardLabel(card) : 'esta tarjeta';

    this.confirmationService.confirm({
      header: 'Eliminar tarjeta',
      message: `¿Quieres eliminar ${label}? Esta acción no se puede deshacer.`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, eliminar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        void this.executeDeleteCard(cardId);
      }
    });
  }

  /**
   * Llama al endpoint público de eliminación y actualiza la lista localmente sin volver
   * a consultar al servidor. La autorización se prueba en el backend con el token de la
   * suscripción.
   */
  private async executeDeleteCard(cardId: number): Promise<void> {
    const token = this.route.snapshot.paramMap.get('token');
    if (!token) return;

    this.deletingCardId.set(cardId);
    this.errorMessage.set('');

    try {
      await firstValueFrom(this.subscriptionService.deletePublicCustomerCard(token, cardId));

      const remaining = this.customerCards().filter(c => c.id !== cardId);
      this.customerCards.set(remaining);

      if (this.selectedCardId() === cardId) {
        this.selectedCardId.set(remaining[0]?.id ?? null);
      }

      if (remaining.length === 0) {
        this.cardPaymentMode.set('new');
        this.applyPaymentModeValidators('new');
      }
    } catch (err: unknown) {
      const e = err as { error?: { message?: string }; message?: string } | null;
      this.errorMessage.set(e?.error?.message ?? e?.message ?? 'No se pudo eliminar la tarjeta.');
    } finally {
      this.deletingCardId.set(null);
    }
  }

  get hasReachedCardLimit(): boolean {
    return this.customerCards().length >= this.maxCustomerCards;
  }

  private applyPaymentModeValidators(mode: CardPaymentMode): void {
    const cardholderName = this.checkoutForm.get('cardholderName');
    const cardNumber = this.checkoutForm.get('cardNumber');
    const expirationDate = this.checkoutForm.get('expirationDate');
    const cvv = this.checkoutForm.get('cvv');

    if (mode === 'existing') {
      cardholderName?.clearValidators();
      cardNumber?.clearValidators();
      expirationDate?.clearValidators();
      cvv?.setValidators([Validators.required, Validators.minLength(3), Validators.maxLength(4)]);
    } else {
      cardholderName?.setValidators([Validators.required, Validators.maxLength(200)]);
      cardNumber?.setValidators([Validators.required, Validators.minLength(13), Validators.maxLength(19)]);
      expirationDate?.setValidators([Validators.required, Validators.pattern(/^(0[1-9]|1[0-2])\/\d{2}$/)]);
      cvv?.setValidators([Validators.required, Validators.minLength(3), Validators.maxLength(4)]);
    }

    cardholderName?.updateValueAndValidity({ emitEvent: false });
    cardNumber?.updateValueAndValidity({ emitEvent: false });
    expirationDate?.updateValueAndValidity({ emitEvent: false });
    cvv?.updateValueAndValidity({ emitEvent: false });
  }

  isCheckoutFormValid(): boolean {
    if (this.requiresInvoice && !this.checkoutForm.get('cfdiUseId')?.valid) {
      return false;
    }
    if (!this.checkoutForm.get('cvv')?.valid) {
      return false;
    }
    if (this.cardPaymentMode() === 'existing') {
      return this.selectedCardId() != null;
    }
    const cardFields = ['cardholderName', 'cardNumber', 'expirationDate'] as const;
    return cardFields.every(name => this.checkoutForm.get(name)?.valid);
  }

  async onCheckout(): Promise<void> {
    this.checkoutLoading.set(true);
    this.errorMessage.set('');

    if (!this.isCheckoutFormValid()) {
      this.checkoutForm.markAllAsTouched();
      this.errorMessage.set(
        this.requiresInvoice
          ? 'Revisa los datos de facturación y de la tarjeta antes de continuar.'
          : 'Revisa los campos de la tarjeta antes de continuar.');
      this.checkoutLoading.set(false);
      return;
    }

    if (this.cardPaymentMode() === 'existing' && this.selectedCardId() == null) {
      this.errorMessage.set('Selecciona una tarjeta guardada para continuar.');
      this.checkoutLoading.set(false);
      return;
    }

    await Promise.resolve();

    if (this.cardPaymentMode() === 'existing') {
      await this.checkoutWithExistingCard();
      return;
    }

    await this.checkoutWithNewCard();
  }

  /**
   * Construye el payload de checkout: envía customerCardId (tarjeta existente)
   * o cardToken (tarjeta nueva tokenizada), nunca ambos.
   */
  private buildCheckoutRequest(
    referenceId: string,
    netPay: NetPaySdk,
    options: { cardToken?: string; tokenProviderPayload?: Record<string, unknown> }
  ): PublicSubscriptionCheckoutRequest {
    const sub = this.subscription();
    const cvvVal = String(this.checkoutForm.get('cvv')?.value ?? '').trim();
    const raw = this.checkoutForm.getRawValue() as {
      requiresInvoice?: boolean;
      cfdiUseId?: string | null;
    };

    const base: PublicSubscriptionCheckoutRequest = {
      customerName: this.subscriberName(sub),
      customerEmail: this.subscriberEmail(sub),
      customerPhone: sub?.customer?.phone?.trim() ?? '',
      cvv: cvvVal,
      referenceId,
      userAgent: this.userAgent || undefined,
      deviceInformation: netPay.form?.deviceInformation?.() ?? undefined,
      deviceFingerPrint: this.netpayDeviceFingerPrint() ?? undefined,
      ...(raw.requiresInvoice && raw.cfdiUseId != null
        ? { cfdiUseId: String(raw.cfdiUseId) }
        : {})
    };

    if (this.cardPaymentMode() === 'existing') {
      const cardId = this.selectedCardId();
      if (cardId == null) {
        throw new Error('No hay tarjeta seleccionada.');
      }
      return { ...base, customerCardId: cardId };
    }

    const cardToken = options.cardToken?.trim();
    if (!cardToken) {
      throw new Error('No se obtuvo el token de la tarjeta nueva.');
    }
    return {
      ...base,
      cardToken,
      tokenProviderPayload: options.tokenProviderPayload
    };
  }

  private async checkoutWithExistingCard(): Promise<void> {
    const win = this.document.defaultView as (Window & {
      NetPay?: NetPaySdk;
      netpay3ds?: NetPay3dsSdk;
    }) | null;

    const netPay = win?.NetPay as NetPaySdk;
    const netpay3ds = win?.netpay3ds as NetPay3dsSdk;

    if (!netpay3ds || !netPay) {
      this.checkoutLoading.set(false);
      this.errorMessage.set('No se pudo inicializar NetPay. Recarga la página e intenta nuevamente.');
      return;
    }

    netpay3ds.setApiKey?.(environment.netpayPublicKey);
    netpay3ds.setSandboxMode?.(environment.netpaySandboxMode);
    netPay.setSandboxMode?.(environment.netpaySandboxMode);
    netPay.setApiKey?.(environment.netpayPublicKey);

    const callback3ds = (_ctx: unknown, referenceId: string) => {
      this.netpay3dsContext = _ctx;
      const subscriptionToken = this.route.snapshot.paramMap.get('token')!;
      try {
        const checkoutRequest = this.buildCheckoutRequest(referenceId, netPay, {});
        void this.submitCheckout(subscriptionToken, checkoutRequest);
      } catch (err: unknown) {
        this.checkoutLoading.set(false);
        this.errorMessage.set(err instanceof Error ? err.message : 'No se pudo preparar el pago.');
      }
    };

    const amount = this.subscription()?.total ?? 0;
    netpay3ds.setSandboxMode(environment.netpaySandboxMode);
    netpay3ds.init((_this: unknown) => {
      netpay3ds.config(_this, amount, callback3ds);
    });
  }

  private async checkoutWithNewCard(): Promise<void> {
    const win = this.document.defaultView as (Window & {
      NetPay?: NetPaySdk;
      netpay3ds?: NetPay3dsSdk;
    }) | null;

    const netPay = win?.NetPay as NetPaySdk;
    const netpay3ds = win?.netpay3ds as NetPay3dsSdk;

    if (!netpay3ds || !netPay) {
      this.checkoutLoading.set(false);
      this.errorMessage.set('No se pudo inicializar NetPay. Recarga la página e intenta nuevamente.');
      return;
    }

    netpay3ds.setApiKey?.(environment.netpayPublicKey);
    netpay3ds.setSandboxMode?.(environment.netpaySandboxMode);
    netPay.setSandboxMode?.(environment.netpaySandboxMode);
    netPay.setApiKey?.(environment.netpayPublicKey);

    const cardholderName = String(this.checkoutForm.get('cardholderName')?.value ?? '').trim();
    const cardNumber = String(this.checkoutForm.get('cardNumber')?.value ?? '').replace(/\s+/g, '');
    const cvv = String(this.checkoutForm.get('cvv')?.value ?? '').trim();
    const expiration = String(this.checkoutForm.get('expirationDate')?.value ?? '').trim();
    const [expMonth = '', expYearShort = ''] = expiration.split('/');
    const expYear = expYearShort.length === 2 ? `20${expYearShort}` : expYearShort;

    const cardInformation: NetPayToken = {
      cardHolderName: cardholderName,
      cardNumber,
      expMonth,
      expYear,
      cvv2: cvv,
      vault: false,
      simpleUse: true,
      userAgent: this.userAgent,
      deviceFingerPrint: this.netpayDeviceFingerPrint() ?? ''
    } as NetPayToken;

    const validateNumber = netPay.card.validateNumber(cardInformation.cardNumber);
    const validateExpiry = netPay.card.validateExpiry(cardInformation.expMonth, cardInformation.expYear);
    const validateCVV = netPay.card.validateCVV(cardInformation.cvv2, cardInformation.cardNumber);
    const validateNumberLength = netPay.card.validateNumberLength(cardInformation.cardNumber);

    if (!validateNumberLength || !validateNumber || !validateExpiry || !validateCVV) {
      this.checkoutLoading.set(false);
      this.errorMessage.set('Revisa los campos de la tarjeta antes de continuar.');
      return;
    }

    let tokenizationResponse: NetPayTokenResponse | null = null;
    try {
      tokenizationResponse = await new Promise<NetPayTokenResponse>((resolve, reject) => {
        netPay.token.create(cardInformation, (response: NetPayTokenResponse) => {
          try {
            const data = JSON.parse(response.message.data) as NetPayTokenResponseData;
            response.message.dataParse = data;
            resolve(response);
          } catch (e) {
            reject(e);
          }
        }, (error: unknown) => {
          reject(error);
        });
      });
    } catch {
      this.checkoutLoading.set(false);
      this.errorMessage.set('No se pudo tokenizar la tarjeta. Verifica los datos e intenta de nuevo.');
      return;
    }

    const dataParse = tokenizationResponse?.message?.dataParse;
    const cardToken = dataParse?.token;
    if (!cardToken) {
      this.checkoutLoading.set(false);
      this.errorMessage.set('No se pudo tokenizar la tarjeta. Verifica los datos e intenta de nuevo.');
      return;
    }

    netpay3ds.setSandboxMode(environment.netpaySandboxMode);

    const callback3ds = (_ctx: unknown, referenceId: string) => {
      this.netpay3dsContext = _ctx;
      const subscriptionToken = this.route.snapshot.paramMap.get('token')!;
      try {
        const checkoutRequest = this.buildCheckoutRequest(referenceId, netPay, {
          cardToken,
          tokenProviderPayload: {
            ...(dataParse as Record<string, unknown>),
            status: tokenizationResponse?.status
          }
        });
        void this.submitCheckout(subscriptionToken, checkoutRequest);
      } catch (err: unknown) {
        this.checkoutLoading.set(false);
        this.errorMessage.set(err instanceof Error ? err.message : 'No se pudo preparar el pago.');
      }
    };

    const amount = this.subscription()?.total ?? 0;
    netpay3ds.init((_this: unknown) => {
      netpay3ds.config(_this, amount, callback3ds);
    });
  }

  private submitCheckout(subscriptionToken: string, checkoutRequest: PublicSubscriptionCheckoutRequest): void {
    void firstValueFrom(this.subscriptionService.checkoutSubscription(subscriptionToken, checkoutRequest))
      .then((res) => {
        const body = res?.body;
        if (!body) {
          this.errorMessage.set(res?.message ?? 'No se pudo completar la suscripción.');
          this.checkoutLoading.set(false);
          this.refreshCustomerCardsAfterError(subscriptionToken);
          return;
        }

        this.subscription.set(body);
        const d = this.detail();
        if (d) {
          this.detail.set({ ...d, subscription: body });
        }

        console.log("body.threeDSChallenge", body.threeDSChallenge);
        if (body.threeDSChallenge) {
          this.handleThreeDSChallenge(subscriptionToken, body.threeDSChallenge);
          return;
        }

        this.checkoutSuccess.set(true);
        this.checkoutLoading.set(false);
      })
      .catch((err: { error?: { message?: string }; message?: string } | null) => {
        this.errorMessage.set(err?.error?.message ?? err?.message ?? 'No se pudo completar el pago.');
        this.checkoutLoading.set(false);
        this.refreshCustomerCardsAfterError(subscriptionToken);
      });
  }

  /**
   * Ejecuta el flujo 3DS en el cliente conforme a la documentación de NetPay:
   * - {@code netpay3ds.canProceed(...)} para decidir si abrir el ACS;
   * - {@code netpay3ds.proceed(_this, ...)} para lanzar el reto.
   *
   * <p>Nota sobre {@code _this}: revisando la implementación del SDK
   * (https://cdn.netpay.mx/js/dev/netpay3ds.js), el primer argumento de
   * {@code proceed} es un pasajero que el SDK solo regresa al callback final —
   * internamente no lo usa. Lo que abre el iframe es {@code Cardinal.continue(...)},
   * que requiere que {@code Cardinal} esté inicializado vía el {@code config()}
   * previo del flujo de captura (en {@code callback3ds}). Pasamos el contexto
   * capturado si existe pero no es bloqueante.</p>
   *
   * <p>Si {@code canProceed} es falso, confirmamos directo con
   * {@code processorTransactionId = null}.</p>
   */
  private handleThreeDSChallenge(subscriptionToken: string, challenge: ThreeDSChallengeDTO): void {
    const win = this.document.defaultView as (Window & {
      netpay3ds?: NetPay3dsSdk;
      Cardinal?: unknown;
    }) | null;
    const netpay3ds = win?.netpay3ds as NetPay3dsSdk | undefined;

    if (!netpay3ds) {
      this.errorMessage.set('No se pudo inicializar la validación 3D Secure. Recarga la página e intenta nuevamente.');
      this.checkoutLoading.set(false);
      return;
    }

    if (!win?.Cardinal) {
      this.errorMessage.set('La sesión 3D Secure aún no está lista (Cardinal no cargado). Recarga la página e intenta nuevamente.');
      this.checkoutLoading.set(false);
      return;
    }

    try {

      console.log("challenge", challenge);
      console.log("subscriptionToken", subscriptionToken);

      const canProceed = netpay3ds.canProceed(challenge.chargeStatus, challenge.responseCode, challenge.acsUrl);
      console.log("canProceed", canProceed);

      if (!canProceed) {
        console.log("no canProceed submitConfirm3DS");
        this.submitConfirm3DS(subscriptionToken, null);
        return;
      }

      console.log("canProceed submitConfirm3DS");
      netpay3ds.proceed(
        this.netpay3dsContext,
        challenge.acsUrl,
        challenge.paReq,
        challenge.authenticationTransactionID,
        (_ctx: unknown, processorTransactionId: string | null, status: string) => {
          console.log("proceed");
          if (status === 'success' && processorTransactionId) {
            this.submitConfirm3DS(subscriptionToken, processorTransactionId);
          } else {
            this.errorMessage.set('La validación 3D Secure fue rechazada por tu banco.');
            this.checkoutLoading.set(false);
            this.refreshCustomerCardsAfterError(subscriptionToken);
          }
        }
      );

    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'No se pudo iniciar la validación 3D Secure.';
      console.error("error", msg);
      this.errorMessage.set(msg);
      this.checkoutLoading.set(false);
      this.refreshCustomerCardsAfterError(subscriptionToken);
    }
  }

  /**
   * Llama al endpoint del backend que confirma el cargo contra NetPay y activa la suscripción.
   */
  private submitConfirm3DS(subscriptionToken: string, processorTransactionId: string | null): void {
    void firstValueFrom(this.subscriptionService.confirmSubscription3DS(subscriptionToken, { processorTransactionId }))
      .then((res) => {
        if (res?.body) {
          this.subscription.set(res.body);
          const d = this.detail();
          if (d) {
            this.detail.set({ ...d, subscription: res.body });
          }
          this.checkoutSuccess.set(true);
        } else {
          this.errorMessage.set(res?.message ?? 'No se pudo confirmar la suscripción.');
          this.refreshCustomerCardsAfterError(subscriptionToken);
        }
      })
      .catch((err: { error?: { message?: string }; message?: string }) => {
        this.errorMessage.set(err?.error?.message ?? err?.message ?? 'No se pudo confirmar la suscripción.');
        this.refreshCustomerCardsAfterError(subscriptionToken);
      })
      .finally(() => {
        this.checkoutLoading.set(false);
      });
  }

  private async initializeNetpaySdk(): Promise<void> {
    if (this.netpayReadyWait) {
      return this.netpayReadyWait;
    }

    const win = this.document.defaultView as (Window & {
      NetPay?: NetPaySdk;
      netpay3ds?: unknown;
    }) | null;
    if (!win) return;

    this.netpayReadyWait = (async () => {
      try {
        this.NetPayElement = await this.loadScriptByUrl(environment.netpayCdnUrl, 'netpay-cdn-script');
        this.netpay3dsElement = await this.loadScriptByUrl(environment.netpay3dsCdnUrl, 'netpay3ds-cdn-script');

        if (!win.NetPay || !win.netpay3ds) {
          throw new Error('No se pudo inicializar NetPay. Recarga la página e intenta nuevamente.');
        }

        if (typeof win.NetPay.setApiKey === 'function') {
          win.NetPay.setApiKey(environment.netpayPublicKey);
        }

        const fingerprint = win.NetPay.form?.generateDeviceFingerPrint?.() ?? null;
        this.netpayDeviceFingerPrint.set(fingerprint);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'No se pudieron cargar los scripts de NetPay.';
        this.errorMessage.set(msg);
      }
    })();

    return this.netpayReadyWait;
  }

  private loadScriptByUrl(url: string, scriptId: string): Promise<HTMLScriptElement> {
    const existing = this.document.getElementById(scriptId) as HTMLScriptElement | null;
    if (existing) {
      if ((existing as HTMLScriptElement & { dataset?: DOMStringMap }).dataset?.loaded === 'true') {
        return Promise.resolve(existing);
      }
      return new Promise((resolve, reject) => {
        existing.addEventListener('load', () => resolve(existing), { once: true });
        existing.addEventListener('error', () => reject(new Error(`No se pudo cargar ${url}`)), { once: true });
      });
    }

    const script = this.document.createElement('script');
    script.id = scriptId;
    script.src = url;
    script.async = true;
    script.defer = true;

    return new Promise((resolve, reject) => {
      script.addEventListener('load', () => {
        script.dataset.loaded = 'true';
        resolve(script);
      }, { once: true });
      script.addEventListener('error', () => reject(new Error(`No se pudo cargar ${url}`)), { once: true });
      this.document.body.appendChild(script);
    });
  }

  /** Nombre visible: el API ya no envía `customerName` plano, solo `customer`. */
  subscriberName(sub: SubscriptionDTO | null | undefined): string {
    const customer = sub?.customer;
    return (
      customer?.comercialName?.trim()
      || customer?.legalName?.trim()
      || sub?.customerName?.trim()
      || ''
    );
  }

  /** Correo del suscriptor: el API lo entrega en `customer.email`. */
  subscriberEmail(sub: SubscriptionDTO | null | undefined): string {
    return sub?.customer?.email?.trim() || sub?.customerEmail?.trim() || '';
  }

  hasSubscriberContact(sub: SubscriptionDTO | null | undefined): boolean {
    return !!this.subscriberName(sub) && !!this.subscriberEmail(sub);
  }

  get isPendingPayment(): boolean {
    return this.subscription()?.status === 'PENDING_PAYMENT';
  }

  get isActive(): boolean {
    return this.subscription()?.status === 'ACTIVE';
  }

  get isCancelled(): boolean {
    const s = this.subscription()?.status;
    return s === 'CANCELLED' || s === 'EXPIRED';
  }

  get planItems(): PlanItemDTO[] {
    return this.subscription()?.plan?.items ?? [];
  }

  get currency(): string {
    return this.subscription()?.currency ?? 'MXN';
  }

  get hasSavedCards(): boolean {
    return this.customerCards().length > 0;
  }

  formatCardExpiration(card: CustomerCardDTO): string {
    const month = card.expirationMonth?.padStart(2, '0') ?? '—';
    const year = card.expirationYear ?? '';
    const shortYear = year.length === 4 ? year.slice(-2) : year;
    return shortYear ? `${month}/${shortYear}` : month;
  }

  formatCardLabel(card: CustomerCardDTO): string {
    const brand = card.brand?.trim() || 'Tarjeta';
    const digits = card.lastDigits?.trim() || '****';
    return `${brand} •••• ${digits}`;
  }

  onExpirationKeydown(event: KeyboardEvent): void {
    if (event.defaultPrevented) return;
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    const key = event.key;
    if (key.length !== 1 || key < '0' || key > '9') return;

    const input = event.target as HTMLInputElement;
    const start = input.selectionStart ?? 0;
    const end = input.selectionEnd ?? start;
    const val = input.value;

    if (start < 3 && end > start) {
      return;
    }

    if (start >= 3) return;

    const first = val.charAt(0);

    if (start === 0) {
      if (key !== '0' && key !== '1') {
        event.preventDefault();
      }
      return;
    }

    if (start === 1 && end === 1) {
      if (!/\d/.test(first)) return;
      if (first === '0') {
        if (key === '0') event.preventDefault();
      } else if (first === '1') {
        if (key > '2') event.preventDefault();
      }
    }
  }

  onExpirationInput(): void {
    const c = this.checkoutForm.get('expirationDate');
    if (!c) return;
    const raw = (c.value as string) ?? '';
    const digits = raw.replace(/\D/g, '');
    if (digits.length < 2) return;

    const origMm = parseInt(digits.slice(0, 2), 10);
    if (origMm >= 1 && origMm <= 12) return;

    let mm = origMm;
    if (mm > 12) mm = 12;
    if (mm < 1) mm = 1;
    const mmStr = mm.toString().padStart(2, '0');
    const yy = digits.slice(2, 4);

    let desired: string;
    if (digits.length >= 4) {
      desired = `${mmStr}/${yy}`;
    } else if (digits.length === 3) {
      desired = `${mmStr}/${digits.slice(2)}`;
    } else {
      desired = `${mmStr}/`;
    }

    if (desired !== raw) {
      c.setValue(desired, { emitEvent: false });
      c.updateValueAndValidity();
    }
  }

  formatAmount(amount: number | undefined, curr?: string): string {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: curr || this.currency || 'MXN'
    }).format(amount ?? 0);
  }

  formatDate(isoDate: string | undefined): string {
    if (!isoDate) return '—';
    return new Intl.DateTimeFormat('es-MX', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(new Date(isoDate));
  }

  getFrequencyLabel(freq: string | undefined): string {
    const labels: Record<string, string> = {
      ONCE: 'Único',
      DAILY: 'Diario',
      WEEKLY: 'Semanal',
      BIWEEKLY: 'Quincenal',
      MONTHLY: 'Mensual',
      QUARTERLY: 'Trimestral',
      SEMIANNUALLY: 'Semestral',
      ANNUALLY: 'Anual'
    };
    return freq ? labels[freq] ?? freq : '—';
  }

  getStatusLabel(status: string | undefined): string {
    const labels: Record<string, string> = {
      PENDING_PAYMENT: 'Pendiente de pago',
      ACTIVE: 'Activa',
      PAUSED: 'Pausada',
      CANCELLED: 'Cancelada',
      EXPIRED: 'Expirada'
    };
    return status ? labels[status] ?? status : '—';
  }
}
